import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';

interface UserPermissions {
  userId: string;
  email: string;
  canEditListings: boolean;
  assignedStates: string[];
  assignedCities: string[];
  isAdmin: boolean;
}

interface AdminContextType {
  isAdmin: boolean;
  userPermissions: UserPermissions | null;
  allUserPermissions: UserPermissions[];
  firstUserRegistered: boolean;
  updateUserPermissions: (userId: string, permissions: Partial<UserPermissions>) => void;
  checkPermissions: (stateName?: string, cityName?: string) => boolean;
  addUserPermission: (user: UserPermissions) => void;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

interface AdminProviderProps {
  children: React.ReactNode;
}

export const AdminProvider: React.FC<AdminProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [userPermissions, setUserPermissions] = useState<UserPermissions | null>(null);
  const [allUserPermissions, setAllUserPermissions] = useState<UserPermissions[]>([]);
  const [firstUserRegistered, setFirstUserRegistered] = useState(false);

  // Load permissions from localStorage
  useEffect(() => {
    const storedPermissions = localStorage.getItem('userPermissions');
    const storedFirstUser = localStorage.getItem('firstUserRegistered');

    if (storedPermissions) {
      const permissions = JSON.parse(storedPermissions);
      setAllUserPermissions(permissions);
    }

    if (storedFirstUser) {
      setFirstUserRegistered(JSON.parse(storedFirstUser));
    }
  }, []);

  // Check admin status when user changes
  useEffect(() => {
    if (currentUser) {
      // If this is the first user ever, make them admin
      if (!firstUserRegistered) {
        const adminUser: UserPermissions = {
          userId: currentUser.uid,
          email: currentUser.email || '',
          canEditListings: true,
          assignedStates: [],
          assignedCities: [],
          isAdmin: true
        };

        setAllUserPermissions([adminUser]);
        setUserPermissions(adminUser);
        setIsAdmin(true);
        setFirstUserRegistered(true);

        // Persist to localStorage
        localStorage.setItem('userPermissions', JSON.stringify([adminUser]));
        localStorage.setItem('firstUserRegistered', 'true');
      } else {
        // Check if user has existing permissions
        const existingPermissions = allUserPermissions.find(
          p => p.userId === currentUser.uid
        );

        if (existingPermissions) {
          setUserPermissions(existingPermissions);
          setIsAdmin(existingPermissions.isAdmin);
        } else {
          // New user - create basic permissions
          const newUser: UserPermissions = {
            userId: currentUser.uid,
            email: currentUser.email || '',
            canEditListings: false,
            assignedStates: [],
            assignedCities: [],
            isAdmin: false
          };

          const updatedPermissions = [...allUserPermissions, newUser];
          setAllUserPermissions(updatedPermissions);
          setUserPermissions(newUser);
          setIsAdmin(false);

          localStorage.setItem('userPermissions', JSON.stringify(updatedPermissions));
        }
      }
    } else {
      setUserPermissions(null);
      setIsAdmin(false);
    }
  }, [currentUser, allUserPermissions, firstUserRegistered]);

  const updateUserPermissions = (userId: string, permissions: Partial<UserPermissions>) => {
    const updatedPermissions = allUserPermissions.map(user =>
      user.userId === userId ? { ...user, ...permissions } : user
    );

    setAllUserPermissions(updatedPermissions);
    localStorage.setItem('userPermissions', JSON.stringify(updatedPermissions));

    // Update current user's permissions if they're the one being updated
    if (currentUser && currentUser.uid === userId) {
      const updatedUserPermissions = updatedPermissions.find(p => p.userId === userId);
      if (updatedUserPermissions) {
        setUserPermissions(updatedUserPermissions);
        setIsAdmin(updatedUserPermissions.isAdmin);
      }
    }
  };

  const addUserPermission = (user: UserPermissions) => {
    const updatedPermissions = [...allUserPermissions, user];
    setAllUserPermissions(updatedPermissions);
    localStorage.setItem('userPermissions', JSON.stringify(updatedPermissions));
  };

  const checkPermissions = (stateName?: string, cityName?: string): boolean => {
    if (!currentUser || !userPermissions) return false;
    if (isAdmin) return true;
    if (!userPermissions.canEditListings) return false;

    // If no specific location, check general edit permissions
    if (!stateName && !cityName) return userPermissions.canEditListings;

    // Check state-specific permissions
    if (stateName && userPermissions.assignedStates.includes(stateName)) return true;

    // Check city-specific permissions
    if (cityName && userPermissions.assignedCities.includes(cityName)) return true;

    return false;
  };

  const value: AdminContextType = {
    isAdmin,
    userPermissions,
    allUserPermissions,
    firstUserRegistered,
    updateUserPermissions,
    checkPermissions,
    addUserPermission
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};